/**
 * Mock game engine (PRD §18, §19, §32).
 *
 * A pure, deterministic state machine. Given a starting state and an
 * ordered script of plays, `applyEvent` derives every piece of domain
 * state (score, down, distance, ball position, possession) from real
 * football rules. Nothing is stored as a presentation string
 * (Implementation Guide §24).
 *
 * Coordinate system
 * -----------------
 * `ballPosition` is 0–100 yards measured from the POSSESSING team's own
 * goal line: 0 = their own goal line, 100 = the opponent's goal line.
 * "Yards to the end zone" is therefore `100 - ballPosition`, and the
 * value mirrors cleanly on a change of possession.
 *
 * Drives are not nested inside the state; every event carries the
 * `driveNumber` that was active when the play began, and `groupIntoDrives`
 * reconstructs them. That keeps the reducer simple and means a play that
 * ends a drive (a punt, an interception) stays in the drive it belongs to.
 *
 * Not modelled in the MVP: safeties, two-minute warning, overtime rules,
 * onside kicks, replay reviews. The scripted games never produce them.
 */

import { EVENT_TYPES } from "../data/concepts/eventConceptMap";

/**
 * Every state a game can be in, ours rather than any vendor's.
 *
 * The first three are the only ones the mock engine ever produces: a
 * scripted game is scheduled, then running, then over. The rest exist
 * because a real feed publishes them and the alternative was quietly
 * filing them under `SCHEDULED`, which is how a called-off fixture ends
 * up on screen as tonight's game (see espnNormalizer.normalizeStatusType).
 *
 * `UNKNOWN` is not a failure state to be tidied away. It is the honest
 * answer when a provider tells us something we do not recognise, and it
 * is deliberately not `SCHEDULED`, so nothing downstream can treat an
 * unrecognised row as a fixture that is about to be played.
 */
export const GAME_STATUS = Object.freeze({
  SCHEDULED: "scheduled",
  IN_PROGRESS: "in_progress",
  FINAL: "final",

  POSTPONED: "postponed",
  CANCELLED: "cancelled",
  SUSPENDED: "suspended",
  DELAYED: "delayed",

  UNKNOWN: "unknown",
});

/**
 * Statuses that mean "this is not a fixture you can follow as listed".
 *
 * Everything here shares one property that matters to the UI: a kickoff
 * time is not the useful thing to print, and any score attached to it is
 * either absent or a partial one that must not be shown as a result.
 */
const OFF_SCHEDULE_STATUSES = new Set([
  GAME_STATUS.POSTPONED,
  GAME_STATUS.CANCELLED,
  GAME_STATUS.SUSPENDED,
  GAME_STATUS.DELAYED,
  GAME_STATUS.UNKNOWN,
]);

/** @param {string} status @returns {boolean} */
export const isOffSchedule = (status) => OFF_SCHEDULE_STATUSES.has(status);

export const QUARTER_SECONDS = 15 * 60;
export const KICKOFF_TOUCHBACK_POSITION = 25;
export const PUNT_TOUCHBACK_POSITION = 20;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/* ------------------------------------------------------------------ */
/* initial state                                                       */
/* ------------------------------------------------------------------ */

export function createInitialGameState(game) {
  return {
    gameId: game.gameId,
    status: GAME_STATUS.SCHEDULED,

    quarter: 1,
    clock: QUARTER_SECONDS,

    homeTeamId: game.homeTeamId,
    awayTeamId: game.awayTeamId,
    homeScore: 0,
    awayScore: 0,

    possession: null,
    down: null,
    distance: null,
    ballPosition: null,

    driveNumber: 0,
    driveTeamId: null,
    driveStartPosition: null,

    latestEvent: null,
    playIndex: -1,

    firstScoringTeamId: null,
    hadTurnover: false,
  };
}

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

export const opponentOf = (state, teamId) =>
  teamId === state.homeTeamId ? state.awayTeamId : state.homeTeamId;

function addScore(state, teamId, points) {
  const isHome = teamId === state.homeTeamId;
  return {
    homeScore: state.homeScore + (isHome ? points : 0),
    awayScore: state.awayScore + (isHome ? 0 : points),
    firstScoringTeamId: state.firstScoringTeamId ?? teamId,
  };
}

/** A fresh set of downs at `position` (0–99, possessing team's frame). */
function freshDowns(position) {
  const ballPosition = clamp(Math.round(position), 1, 99);
  return {
    down: 1,
    // Inside the 10 the target is the goal line, not a 10-yard marker.
    distance: Math.min(10, 100 - ballPosition),
    ballPosition,
  };
}

/** Give the ball to `teamId` at `position` and open a new drive. */
function beginDrive(state, teamId, position, { turnover = false } = {}) {
  const downs = freshDowns(position);
  return {
    possession: teamId,
    ...downs,
    driveNumber: state.driveNumber + 1,
    driveTeamId: teamId,
    driveStartPosition: downs.ballPosition,
    hadTurnover: state.hadTurnover || turnover,
  };
}

/**
 * Move the ball and derive the resulting down and distance.
 * @returns one of: {touchdown}, {firstDown, ...downs}, {turnoverOnDowns, ...},
 *          or {down, distance, ballPosition}
 */
function advanceBall(state, yards) {
  const raw = state.ballPosition + yards;

  if (raw >= 100) return { touchdown: true };

  const ballPosition = clamp(raw, 1, 99);
  const distance = state.distance - yards;

  if (distance <= 0) {
    return { firstDown: true, ...freshDowns(ballPosition) };
  }

  if (state.down >= 4) {
    return { turnoverOnDowns: true, ballPosition };
  }

  return {
    down: state.down + 1,
    distance: Math.min(distance, 100 - ballPosition),
    ballPosition,
  };
}

/* ------------------------------------------------------------------ */
/* event application                                                   */
/* ------------------------------------------------------------------ */

/**
 * Apply one scripted play.
 *
 * @param {object} state current game state
 * @param {object} play  script entry; see src/data/mockEvents
 * @param {number} index position in the script
 */
export function applyEvent(state, play, index = (state.playIndex ?? -1) + 1) {
  if (!play?.type) return state;

  // The drive a play belongs to is the one that was running when it began.
  const driveNumber = state.driveNumber;
  const actingTeamId = play.teamId ?? state.possession ?? null;

  const base = {
    ...state,
    status: GAME_STATUS.IN_PROGRESS,
    clock: Math.max(0, state.clock - (Number(play.clockUsed) || 0)),
    playIndex: index,
  };

  const { __firstDown, __turnover, ...next } = reduce(base, play);

  const event = {
    id: play.id ?? `${state.gameId}-play-${index}`,
    index,
    driveNumber,
    type: play.type,
    teamId: actingTeamId,
    yards: play.yards ?? null,
    description: play.description ?? "",
    quarter: next.quarter,
    clock: next.clock,
    resultingDown: next.down,
    resultingDistance: next.distance,
    resultingBallPosition: next.ballPosition,
    homeScore: next.homeScore,
    awayScore: next.awayScore,
    pointsScored:
      next.homeScore - state.homeScore + (next.awayScore - state.awayScore),
    firstDown: Boolean(__firstDown),
    turnover: Boolean(__turnover),
  };

  return { ...next, latestEvent: event };
}

function reduce(state, play) {
  switch (play.type) {
    /* ---------------------------------------------- kickoff */
    case EVENT_TYPES.KICKOFF: {
      const receivingTeam =
        play.receivingTeamId ??
        (state.possession ? opponentOf(state, state.possession) : state.awayTeamId);

      return {
        ...state,
        ...beginDrive(
          state,
          receivingTeam,
          Number.isFinite(play.resultPosition)
            ? play.resultPosition
            : KICKOFF_TOUCHBACK_POSITION
        ),
      };
    }

    /* ---------------------------------------------- standard plays */
    case EVENT_TYPES.RUN:
    case EVENT_TYPES.PASS_COMPLETE:
      return resolveGain(state, Number(play.yards ?? 0));

    case EVENT_TYPES.SACK:
      return resolveGain(state, -Math.abs(Number(play.yards ?? 0)));

    case EVENT_TYPES.PASS_INCOMPLETE: {
      if (state.down >= 4) {
        return { ...state, ...turnoverOnDowns(state), __turnover: true };
      }
      return { ...state, down: state.down + 1 };
    }

    /* ---------------------------------------------- penalty */
    case EVENT_TYPES.PENALTY: {
      // `yards` is signed from the offence's point of view: negative for a
      // penalty against them, positive for one against the defence.
      const yards = Number(play.yards ?? 0);
      const ballPosition = clamp(state.ballPosition + yards, 1, 99);

      if (play.automaticFirstDown) {
        return { ...state, ...freshDowns(ballPosition), __firstDown: true };
      }

      const distance = state.distance - yards;
      if (distance <= 0) {
        return { ...state, ...freshDowns(ballPosition), __firstDown: true };
      }

      // A penalty replays the down unless it carries loss of down.
      return {
        ...state,
        down: play.lossOfDown ? Math.min(4, state.down + 1) : state.down,
        distance: Math.min(distance, 100 - ballPosition),
        ballPosition,
      };
    }

    /* ---------------------------------------------- scoring */
    case EVENT_TYPES.TOUCHDOWN:
      return scoreTouchdown(state, play.teamId ?? state.possession);

    case EVENT_TYPES.EXTRA_POINT:
      return play.success === false
        ? state
        : { ...state, ...addScore(state, play.teamId ?? state.possession, 1) };

    case EVENT_TYPES.TWO_POINT_CONVERSION:
      return play.success === false
        ? state
        : { ...state, ...addScore(state, play.teamId ?? state.possession, 2) };

    case EVENT_TYPES.FIELD_GOAL:
      return {
        ...state,
        ...addScore(state, play.teamId ?? state.possession, 3),
        down: null,
        distance: null,
      };

    case EVENT_TYPES.FIELD_GOAL_MISSED: {
      // The defence takes over at the spot of the kick, or their own 20,
      // whichever gives them more field.
      const spot = Math.max(100 - state.ballPosition, PUNT_TOUCHBACK_POSITION);
      return {
        ...state,
        ...beginDrive(state, opponentOf(state, state.possession), spot),
      };
    }

    /* ---------------------------------------------- possession changes */
    case EVENT_TYPES.PUNT: {
      const puntYards = Number(play.puntYards ?? play.yards ?? 40);
      const endSpot = state.ballPosition + puntYards;
      const newPosition =
        endSpot >= 100 ? PUNT_TOUCHBACK_POSITION : clamp(100 - endSpot, 1, 99);

      return {
        ...state,
        ...beginDrive(state, opponentOf(state, state.possession), newPosition),
      };
    }

    case EVENT_TYPES.INTERCEPTION:
    case EVENT_TYPES.FUMBLE: {
      const defence = opponentOf(state, state.possession);

      if (play.returnedForTouchdown) {
        return {
          ...scoreTouchdown(state, defence),
          possession: defence,
          hadTurnover: true,
          __turnover: true,
        };
      }

      return {
        ...state,
        ...beginDrive(
          state,
          defence,
          Number.isFinite(play.resultPosition)
            ? play.resultPosition
            : clamp(100 - state.ballPosition, 1, 99),
          { turnover: true }
        ),
        __turnover: true,
      };
    }

    case EVENT_TYPES.TURNOVER_ON_DOWNS:
      return { ...state, ...turnoverOnDowns(state), __turnover: true };

    /* ---------------------------------------------- clock */
    case EVENT_TYPES.END_OF_QUARTER:
      return { ...state, quarter: state.quarter + 1, clock: QUARTER_SECONDS };

    case EVENT_TYPES.END_OF_GAME:
      return {
        ...state,
        status: GAME_STATUS.FINAL,
        clock: 0,
        down: null,
        distance: null,
        possession: null,
      };

    default:
      return state;
  }
}

function resolveGain(state, yards) {
  const result = advanceBall(state, yards);

  if (result.touchdown) return scoreTouchdown(state, state.possession);

  if (result.turnoverOnDowns) {
    const withBall = { ...state, ballPosition: result.ballPosition };
    return { ...withBall, ...turnoverOnDowns(withBall), __turnover: true };
  }

  return {
    ...state,
    down: result.down,
    distance: result.distance,
    ballPosition: result.ballPosition,
    __firstDown: Boolean(result.firstDown),
  };
}

function scoreTouchdown(state, teamId) {
  return {
    ...state,
    ...addScore(state, teamId, 6),
    possession: teamId,
    ballPosition: 100,
    down: null,
    distance: null,
  };
}

function turnoverOnDowns(state) {
  return beginDrive(
    state,
    opponentOf(state, state.possession),
    clamp(100 - state.ballPosition, 1, 99)
  );
}

/* ------------------------------------------------------------------ */
/* replay                                                              */
/* ------------------------------------------------------------------ */

/** Fold the first `count` plays of a script into a game state. */
export function getStateAtPlay(game, script, count) {
  const total = clamp(count, 0, script.length);
  let state = createInitialGameState(game);

  for (let i = 0; i < total; i += 1) {
    state = applyEvent(state, script[i], i);
  }

  return state;
}

/** Every event produced by the first `count` plays, oldest first. */
export function getEventsUpTo(game, script, count) {
  const total = clamp(count, 0, script.length);
  const events = [];
  let state = createInitialGameState(game);

  for (let i = 0; i < total; i += 1) {
    state = applyEvent(state, script[i], i);
    if (state.latestEvent) events.push(state.latestEvent);
  }

  return events;
}

/**
 * Group a flat event list into drives, newest drive last.
 * @returns {{driveNumber: number, teamId: string, events: object[], result: string}[]}
 */
export function groupIntoDrives(events = []) {
  const drives = [];

  events.forEach((event) => {
    if (event.driveNumber === 0) return; // pre-kickoff bookkeeping
    let drive = drives[drives.length - 1];

    if (!drive || drive.driveNumber !== event.driveNumber) {
      drive = {
        driveNumber: event.driveNumber,
        teamId: event.teamId,
        events: [],
        result: null,
      };
      drives.push(drive);
    }

    drive.events.push(event);
    drive.result = driveResultFor(event) ?? drive.result;
  });

  return drives;
}

function driveResultFor(event) {
  switch (event.type) {
    case EVENT_TYPES.TOUCHDOWN:
      return "Touchdown";
    case EVENT_TYPES.FIELD_GOAL:
      return "Field goal";
    case EVENT_TYPES.FIELD_GOAL_MISSED:
      return "Missed field goal";
    case EVENT_TYPES.PUNT:
      return "Punt";
    case EVENT_TYPES.INTERCEPTION:
      return "Interception";
    case EVENT_TYPES.FUMBLE:
      return "Fumble";
    case EVENT_TYPES.TURNOVER_ON_DOWNS:
      return "Turnover on downs";
    case EVENT_TYPES.END_OF_GAME:
      return "End of game";
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ */
/* presentation helpers (pure)                                         */
/* ------------------------------------------------------------------ */

export function formatClock(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

export const formatQuarter = (quarter) => (quarter > 4 ? "OT" : `Q${quarter}`);

const ORDINALS = { 1: "1st", 2: "2nd", 3: "3rd", 4: "4th" };

/** "3rd & 7", or "3rd & Goal" when the end zone is the target. */
export function formatDownAndDistance(state) {
  if (!state?.down || state.distance == null) return null;

  const goalToGo = state.ballPosition + state.distance >= 100;
  return `${ORDINALS[state.down] ?? `${state.down}th`} & ${
    goalToGo ? "Goal" : state.distance
  }`;
}

/** Broadcast-style field position, e.g. "BUF 35", "CAR 42", "50". */
export function formatBallPosition(state, abbrevOf) {
  if (state?.ballPosition == null || !state.possession) return null;

  const own = state.ballPosition <= 50;
  const yardLine = own ? state.ballPosition : 100 - state.ballPosition;
  if (yardLine === 50) return "50";

  const side = own ? state.possession : opponentOf(state, state.possession);
  return `${abbrevOf(side)} ${yardLine}`;
}

export const yardsToEndZone = (state) =>
  state?.ballPosition == null ? null : 100 - state.ballPosition;

/** Where the chains are, in the same 0–100 frame as ballPosition. */
export const firstDownLine = (state) =>
  state?.ballPosition == null || state.distance == null
    ? null
    : Math.min(100, state.ballPosition + state.distance);

export const totalPoints = (state) =>
  Number(state?.homeScore ?? 0) + Number(state?.awayScore ?? 0);

export function winnerTeamId(state) {
  if (state?.status !== GAME_STATUS.FINAL) return null;
  if (state.homeScore === state.awayScore) return null;
  return state.homeScore > state.awayScore ? state.homeTeamId : state.awayTeamId;
}
